import { useCallback, useEffect, useRef, useState } from "react";
import { Award, Clock, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Spinner } from "@/components/Gate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { errMsg, useApi } from "@/lib/api";

function Take({ a, onDone }) {
  const api = useApi();
  const [s, setS] = useState(null);
  const [ans, setAns] = useState({});
  const [resp, setResp] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api.post(`/learn/assessments/${a.id}/start`).then((r) => setS(r.data)).catch((e) => { toast.error(errMsg(e)); onDone(); });
  }, [api, a.id, onDone]);
  const submit = async () => {
    setBusy(true);
    try { const r = await api.post(`/learn/attempts/${s.attempt_id}/submit`, { answers: ans, response: resp }); setResult(r.data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return <Spinner />;
  if (result) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="attempt-result">
        <p className="text-xl font-bold text-slate-900">{result.status === "pending_review" ? "Submitted for review" : result.status === "passed" ? `Passed · ${result.score}%` : `Not passed · ${result.score}%`}</p>
        {result.late && <p className="mt-2 text-sm text-rose-700">Submitted after the time limit.</p>}
        {result.feedback.filter((f) => f.explanation).map((f) => <p key={f.id} className="mt-2 text-sm text-slate-700">{f.correct ? "Correct" : "Incorrect"}: {f.explanation}</p>)}
        <Button className="mt-4" variant="outline" onClick={onDone} data-testid="attempt-back">Back to assessments</Button>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="attempt-form">
      <h2 className="text-xl font-bold text-slate-900">{a.title}</h2>
      {s.deadline && <p className="mt-1 flex items-center gap-1 text-sm text-slate-700"><Clock className="h-4 w-4" /> Submit by {new Date(s.deadline).toLocaleTimeString()}</p>}
      {s.kind === "practical" ? (
        <div className="mt-4"><p className="whitespace-pre-line text-slate-800">{s.brief}</p><Textarea rows={10} className="mt-3" value={resp} onChange={(e) => setResp(e.target.value)} aria-label="Your submission" data-testid="attempt-response" /></div>
      ) : s.questions.map((q, i) => (
        <fieldset key={q.id} className="mt-5" data-testid={`attempt-q-${i}`}>
          <legend className="font-semibold text-slate-900">{i + 1}. {q.text}</legend>
          {(q.options || []).map((o, j) => (
            <label key={j} className="mt-2 flex items-center gap-2 text-slate-800"><input type="radio" name={q.id} checked={ans[q.id] === j} onChange={() => setAns({ ...ans, [q.id]: j })} data-testid={`attempt-q-${i}-opt-${j}`} /> {o}</label>
          ))}
        </fieldset>
      ))}
      <Button className="mt-6 bg-[#222d99] hover:bg-[#1a237e]" onClick={submit} disabled={busy} data-testid="attempt-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit</Button>
    </div>
  );
}

export default function Assessments() {
  const api = useApi();
  const [list, setList] = useState(null);
  const [certs, setCerts] = useState([]);
  const [taking, setTaking] = useState(null);
  const load = useCallback(() => {
    api.get("/learn/assessments").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/learn/certificates").then((r) => setCerts(r.data)).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const done = useCallback(() => { setTaking(null); load(); }, [load]);
  const download = async (c) => {
    try {
      const r = await api.get(`/learn/certificates/${c.cert_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: `Crowlearn-certificate-${c.cert_id}.pdf` }).click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(e.response?.status === 409 ? "This certificate has been revoked and can't be downloaded" : "Could not download the certificate. Try again."); }
  };
  if (taking) return <Take a={taking} onDone={done} />;
  if (!list) return <Spinner />;
  return (
    <div>
      <PageHeader eyebrow="Prove your skills" title="Assessments & certificates" testid="assessments-title" />
      {!list.length && <p className="rounded-xl border border-slate-200 bg-white p-6 text-slate-700" data-testid="assessments-empty">No assessments yet. They appear here when a course you're enrolled in has one.</p>}
      <div className="grid gap-4 md:grid-cols-2">
        {list.map((a) => (
          <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`assessment-${a.id}`}>
            <div className="flex items-start justify-between gap-2"><h2 className="font-semibold text-slate-900">{a.title}</h2><Badge variant="outline">{a.kind}</Badge></div>
            {a.course_title && <p className="text-sm text-slate-600">{a.course_title}</p>}
            <p className="mt-2 text-sm text-slate-700">Pass mark {a.pass_mark}% · {a.attempts_left} of {a.max_attempts} attempts left{a.time_limit_min ? ` · ${a.time_limit_min} min` : ""}</p>
            {a.last && <p className="mt-1 text-sm text-slate-700">Last result: {a.last.status.replace("_", " ")}{a.last.score != null ? ` (${a.last.score}%)` : ""}</p>}
            {a.passed ? <p className="mt-3 text-sm font-semibold text-emerald-700">Passed</p> : (
              <Button className="mt-3 bg-[#222d99] hover:bg-[#1a237e]" disabled={!a.attempts_left || a.last?.status === "pending_review"} onClick={() => setTaking(a)} data-testid={`assessment-start-${a.id}`}>Start</Button>
            )}
          </div>
        ))}
      </div>
      <h2 className="mt-10 text-xl font-bold text-slate-900">My certificates</h2>
      {!certs.length && <p className="mt-2 text-sm text-slate-600" data-testid="certs-empty">No certificates yet.</p>}
      <ul className="mt-3 space-y-2">
        {certs.map((c) => (
          <li key={c.cert_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`cert-${c.cert_id}`}>
            <Award className="h-5 w-5 text-[#222d99]" /> <span className="font-semibold">{c.title}</span> <span className="font-mono text-sm">{c.cert_id}</span>
            <Badge variant={c.status === "valid" ? "default" : "destructive"}>{c.status}</Badge>
            <a href={`/verify/${c.cert_id}`} className="text-sm text-[#222d99] underline" data-testid={`cert-verify-link-${c.cert_id}`}>Verification page</a>
            {c.status === "valid" && <Button size="sm" variant="outline" onClick={() => download(c)} className="ml-auto" data-testid={`cert-download-${c.cert_id}`}><Download className="mr-1 h-4 w-4" /> Download PDF</Button>}
          </li>
        ))}
      </ul>
    </div>
  );
}
